import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font } from '@react-pdf/renderer';
import Summary from '../models/Summary';

Font.register({
  family: 'Helvetica',
  fonts: [{ src: 'https://fonts.gstatic.com/s/helvetica.ttf' }],
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 9, // ~1/8"
    paddingHorizontal: 24,
    paddingBottom: 24,
    fontFamily: 'Helvetica',
    fontSize: 10,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  title: {
    fontSize: 11,
    fontWeight: 'bold',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    wordBreak: 'keepall',
    overflowWrap: 'normal'
  },
  dateText: {
    fontSize: 10,
    width: '25%',
    textAlign: 'left',
  },
  pageNumber: {
    fontSize: 10,
    width: '25%',
    textAlign: 'right',
  },
  corpNameBlock: {
    marginBottom: 10,
    textAlign: 'left',
    fontSize: 10,
  },
  table: {
    width: '100%',
    borderTop: 1,
    borderColor: '#000',
  },
  tableRow: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: '#000',
    borderLeftWidth: 1,
    borderRightWidth: 1,
  },
  tableRowNoBorder: {
    flexDirection: 'row',
    border: 0
  },
  tableCellHeader: {
    flex: 1,
    paddingVertical: 4,
    paddingHorizontal: 2,
    fontWeight: 'bold',
    backgroundColor: '#fff',
    textAlign: 'center',
  },
  tableCell: {
    flex: 1,
    paddingVertical: 4,
    paddingHorizontal: 2,
    backgroundColor: '#fff',
    textAlign: 'center',
  },
  tableCellWide: {
    flex: 1,
    width: 5,
    paddingVertical: 4,
    paddingHorizontal: 2,
    backgroundColor: '#fff',
    textAlign: 'center',
  },
  periodTotalRow: {
    borderTop: 1.5,
    borderColor: '#000',
    fontWeight: 'bold',
  },
  grandTotalRow: {
    marginTop: 8,
    fontWeight: 'bold',
  },
  grandTotalLabel: {
    flex: 6,
    textAlign: 'center',
  },
});

interface ISummaryDocumentProps {
  summaries: Summary[];
  date: string;
}

const SummaryDocument: React.FC<ISummaryDocumentProps> = ({ summaries, date }) => {
  const first = summaries[0];
  const clientName = first?.clientName ?? '';
  const employeeName = first?.employeeName ?? '';

  const totals = summaries.reduce(
    (acc, t) => {
      acc.days += t.days || 0;
      acc.services += t.services || 0;
      acc.expenses += t.expenses || 0;
      acc.total += t.total || 0;
      return acc;
    },
    { days: 0, services: 0, expenses: 0, total: 0 }
  );

  return (
    <Document>
      <Page size={`A4`} style={styles.page}>
        <View style={styles.headerRow}>
          <Text style={styles.dateText}>Date: {date}</Text>
          <Text style={styles.title}>LANDMAN TIME AND EXPENSE SUMMARY</Text>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>

        <View style={styles.corpNameBlock}>
          <Text>{`CORPORATION: ${clientName}`}</Text>
          <Text>{`NAME: ${employeeName}`}</Text>
        </View>

        <View style={styles.table}>
          <View style={styles.tableRow}>
            {[
              'Period No.',
              'Client No.',
              'Client Name',
              'Pros. No.',
              'Prospect Name',
              'Sec No.',
              'Days',
              'Services',
              'Expenses',
              'Totals',
            ].map((h, idx) => (
              <Text key={idx} style={styles.tableCellHeader}>
                {h}
              </Text>
            ))}
          </View>

          {summaries.map((t, i) => (
            <View key={i} style={styles.tableRow}>
              <Text style={styles.tableCell}>{t.periodNumber ?? ''}</Text>
              <Text style={styles.tableCell}>{t.clientNumber ?? ''}</Text>
              <Text style={styles.tableCellWide}>{t.clientName ?? ''}</Text>
              <Text style={styles.tableCell}>{t.projectNumber ?? ''}</Text>
              <Text style={styles.tableCell}>{t.projectName ?? ''}</Text>
              <Text style={styles.tableCell}>{t.subcode ?? ''}</Text>
              <Text style={styles.tableCell}>{t.days?.toFixed(2) ?? ''}</Text>
              <Text style={styles.tableCell}>{t.services?.toLocaleString() ?? ''}</Text>
              <Text style={styles.tableCell}>{t.expenses?.toLocaleString() ?? ''}</Text>
              <Text style={styles.tableCell}>{t.total?.toLocaleString() ?? ''}</Text>
            </View>
          ))}

          <View style={[styles.tableRow, styles.periodTotalRow]}>
            <Text style={[styles.tableCell, { flex: 6 }]}>Period Totals</Text>
            <Text style={styles.tableCell}>{totals.days.toFixed(2)}</Text>
            <Text style={styles.tableCell}>{totals.services.toLocaleString()}</Text>
            <Text style={styles.tableCell}>{totals.expenses.toLocaleString()}</Text>
            <Text style={styles.tableCell}>{totals.total.toLocaleString()}</Text>
          </View>
        </View>

        {/* Grand totals (no outer border, just spacing below) */}
        <View style={styles.grandTotalRow}>
          <View style={styles.tableRowNoBorder}>
            <Text style={[styles.tableCell, styles.grandTotalLabel]}>Grand Totals</Text>
            <Text style={styles.tableCell}>{totals.days.toFixed(2)}</Text>
            <Text style={styles.tableCell}>{totals.services.toLocaleString()}</Text>
            <Text style={styles.tableCell}>{totals.expenses.toLocaleString()}</Text>
            <Text style={styles.tableCell}>{totals.total.toLocaleString()}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default SummaryDocument;


// WEBPACK FOOTER //
// ./src/components/project-billing/components/TimeAndExpenseSummary.tsx